import { useState } from 'react';
import { api } from '../lib/api';
import { useStore } from '../state/store';

/**
 * The front door, with two quite different audiences.
 *
 * Producers sign in to create and own shows. Crew join with a code and a name
 * and never make an account -- an A2 on a phone backstage, ninety seconds
 * before doors, must not be asked to register.
 */
export default function Landing() {
  const setIdentity = useStore(s => s.setIdentity);
  const setUser = useStore(s => s.setUser);
  const notify = useStore(s => s.notify);

  const [tab, setTab] = useState<'join' | 'signin'>('join');
  const [busy, setBusy] = useState(false);

  // Crew
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<'viewer' | 'editor'>('editor');

  // Producer
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  async function join(e: React.FormEvent) {
    e.preventDefault();
    const c = code.trim().toUpperCase();
    if (!c || !name.trim()) return;
    setBusy(true);
    try {
      const r = await api.join(c, name.trim(), role);
      setIdentity({ code: c, participantId: r.participantId, role: r.role, name: name.trim() });
      if (r.status === 'pending') {
        notify('Waiting for the host to approve editor access. You can watch meanwhile.');
      }
    } catch (err) {
      notify((err as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  }

  async function signIn(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setBusy(true);
    try {
      const r = await api.login(email.trim(), password);
      setUser(r.user);
      setPassword('');
    } catch (err) {
      notify((err as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="landing">
      <div className="landing-card">
        <h1>mic<span style={{ color: 'var(--accent)' }}>Plot</span></h1>
        <p className="sub">Wireless mic assignment and scheduling.</p>

        <div className="tabs" role="tablist">
          <button role="tab" aria-selected={tab === 'join'} onClick={() => setTab('join')}>
            Join a show
          </button>
          <button role="tab" aria-selected={tab === 'signin'} onClick={() => setTab('signin')}>
            Sign in
          </button>
        </div>

        {tab === 'join' ? (
          <form onSubmit={join} className="panel" style={{ padding: 16 }}>
            <div className="field">
              <label htmlFor="code">Show code</label>
              <input id="code" value={code} autoFocus
                     onChange={e => setCode(e.target.value.toUpperCase())}
                     placeholder="e.g. XKTP-9QRM"
                     style={{ fontFamily: 'var(--mono)', letterSpacing: '0.08em' }} />
              <div className="hint">From the producer. No account needed.</div>
            </div>
            <div className="field">
              <label htmlFor="joinname">Your name</label>
              <input id="joinname" value={name} onChange={e => setName(e.target.value)}
                     placeholder="e.g. Jordan" />
            </div>
            <div className="field">
              <label>Role</label>
              <div className="seg">
                <button type="button" aria-pressed={role === 'viewer'}
                        onClick={() => setRole('viewer')}>
                  <strong>Viewer</strong>
                  <div className="hint">See the board. Can't change anything.</div>
                </button>
                <button type="button" aria-pressed={role === 'editor'}
                        onClick={() => setRole('editor')}>
                  <strong>Editor</strong>
                  <div className="hint">Assign mics. Needs host approval.</div>
                </button>
              </div>
            </div>
            <button className="btn primary" style={{ width: '100%' }}
                    disabled={busy || !code.trim() || !name.trim()}>
              {busy ? 'Joining…' : 'Join'}
            </button>
          </form>
        ) : (
          <form onSubmit={signIn} className="panel" style={{ padding: 16 }}>
            <div className="field">
              <label htmlFor="email">Email</label>
              <input id="email" type="email" value={email} autoFocus
                     autoComplete="username"
                     onChange={e => setEmail(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="password">Password</label>
              <input id="password" type="password" value={password}
                     autoComplete="current-password"
                     onChange={e => setPassword(e.target.value)} />
            </div>
            <button className="btn primary" style={{ width: '100%' }}
                    disabled={busy || !email.trim() || !password}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
            <div className="hint" style={{ marginTop: 12 }}>
              Accounts are created by an administrator — there's no public sign-up.
              Ask your producer if you need one.
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
